import { useTranslation } from 'react-i18next';
import React, { useEffect, useRef } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { Loader } from 'lucide-react';
import { logger } from '../utils/logger';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRole?: 'patient' | 'doctor' | 'admin' | 'nurse' | 'clinic' | 'clinic_manager';
  requiredPermissions?: string[];
  fallbackPath?: string;
}

// ✅ ROUTE-1: Permissions de base par rôle (Security Model)
const ROLE_PERMISSIONS: Record<string, string[]> = {
  admin: [
    'view_dashboard',
    'manage_users',
    'view_all_consultations',
    'manage_settings',
    'view_audit_logs',
    'manage_services',
  ],
  doctor: [
    'view_own_consultations',
    'create_consultation',
    'update_own_profile',
    'start_video_call',
  ],
  nurse: [
    'view_triage_queue',
    'claim_triage',
    'record_vitals',
    'complete_handoff',
    'view_patient_records',
  ],
  clinic_manager: [
    'view_clinic_dashboard',
    'manage_site_practitioners',
    'view_triage_queue',
    'manage_referrals',
    'view_dashboard',
  ],
  clinic: [
    'view_clinic_dashboard',
    'manage_site_practitioners',
    'view_triage_queue',
    'manage_referrals',
    'view_dashboard',
  ],
  patient: [
    'view_own_consultations',
    'book_appointment',
    'update_own_profile',
    'join_video_call',
  ],
};

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ 
  children, 
  requiredRole, 
  requiredPermissions = [],
  fallbackPath = '/login' 
}) => {
  const { t } = useTranslation(['common', 'auth']);
  const { user, isAuthenticated, loading: isLoading } = useAuth();
  const location = useLocation();
  const hasLoggedRef = useRef(false);

  // Logger uniquement au premier rendu ou changement de route
  useEffect(() => {
    if (!hasLoggedRef.current && isAuthenticated && user) {
      logger.debug('✅ ProtectedRoute - User authenticated:', user?.role, 'on path:', location.pathname);
      hasLoggedRef.current = true;
    }
  }, [isAuthenticated, user, location.pathname]);

  // Réinitialiser le flag lors du changement de route
  useEffect(() => {
    hasLoggedRef.current = false;
  }, [location.pathname]);

  // Afficher un loader pendant la vérification de l'authentification
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-green-50">
        <div className="text-center">
          <Loader className="animate-spin w-8 h-8 mx-auto mb-4 text-blue-600" />
          <p className="text-slate-600">{t('auth:verification.loading')}</p>
        </div>
      </div>
    );
  }

  // Rediriger vers la page de connexion si non authentifié
  if (!isAuthenticated || !user) {
    // Éviter la boucle infinie en vérifiant si on est déjà sur la page de login
    if (location.pathname !== fallbackPath && location.pathname !== '/login' && location.pathname !== '/register') {
      return <Navigate to={fallbackPath} state={{ from: location }} replace />;
    }
    // Si on est déjà sur login/register, ne pas rediriger
    return null;
  }

  // Vérifier le rôle si requis
  if (requiredRole) {
    // Role verification
    const userRoleRaw = user.role;
    
    if (!userRoleRaw) {
      logger.error('🚫 ROUTE-1: Rôle utilisateur manquant', { userId: user.id });
      return <Navigate to="/login" replace />;
    }

    const userRoleNormalized = userRoleRaw.toLowerCase();
    const isAdminVariant = ['admin', 'saas_admin', 'super_admin'].includes(userRoleNormalized);
    const isClinicVariant = ['clinic_manager', 'clinic'].includes(userRoleNormalized);
    const isClinicStaff = ['nurse', 'doctor', 'clinic_nurse', 'clinic_doctor', 'receptionist'].includes(userRoleNormalized) &&
      (Boolean(localStorage.getItem('activeClinicId')) || Boolean((user as { clinicMemberships?: unknown[] }).clinicMemberships?.length));

    // ✅ SÉCURITÉ: Ségrégation stricte Admin ↔ Clinic Manager
    // - Admin peut accéder à /admin uniquement (il accède à /clinic via nav, mais reste admin)
    // - CLINIC_MANAGER et personnel de clinique (infirmière, médecin) peuvent accéder à /clinic — jamais à /admin
    const isAuthorizedRole =
      userRoleNormalized === requiredRole ||
      // Accès /admin : réservé aux admins variants seulement
      (requiredRole === 'admin' && isAdminVariant) ||
      // Accès /clinic : clinic_manager OU admin (supervision) OU personnel de clinique actif
      ((requiredRole === 'clinic' || requiredRole === 'clinic_manager') &&
        (isClinicVariant || isAdminVariant || isClinicStaff));

    if (!isAuthorizedRole) {
      // Rôle insuffisant : rediriger vers la page dédiée
      logger.warn('🚫 ROUTE-1: Accès refusé - rôle insuffisant', {
        userId: user.id,
        userRole: user.role,
        requiredRole,
        attemptedPath: location.pathname,
      });
      return <Navigate to="/unauthorized" replace />;
    }
    
    // ✅ ROUTE-1: Vérifier les permissions spécifiques (Security Model)
    if (requiredPermissions.length > 0) {
      const userRoleKey = isAdminVariant ? 'admin' : userRoleNormalized;
      const userPermissions = ROLE_PERMISSIONS[userRoleKey] || [];
      
      const hasAllPermissions = requiredPermissions.every(perm => 
        userPermissions.includes(perm)
      );
      
      if (!hasAllPermissions) {
        const missingPermissions = requiredPermissions.filter(perm => 
          !userPermissions.includes(perm)
        );
        logger.warn('🚫 ROUTE-1: Accès refusé - permissions insuffisantes', {
          userId: user.id,
          userRole: user.role,
          requiredPermissions,
          missingPermissions,
          attemptedPath: location.pathname,
        });
        return <Navigate to="/unauthorized" replace />;
      }
      
      logger.debug('✅ ROUTE-1: Permissions vérifiées avec succès', {
        userId: user.id,
        requiredPermissions,
      });
    }
    
    // Role authorized, access granted
  }

  // Afficher le contenu protégé
  return <>{children}</>;
};

export default ProtectedRoute;
